/**
 * Facturación: DTOs de entrada y salida del API `/api/v1` (docs/FASE-6.md §7 y §13.3).
 *
 * Los importes viajan como **texto**, igual que salen de Postgres (`numeric`). No es una rareza:
 * pasarlos por `number` para volver a texto es exactamente el paso donde se pierde el céntimo, y un
 * JSON con `118.00000000000001` dentro es una factura que alguien va a reclamar.
 */
import { z } from "zod";
import { PATTERNS } from "../constants.js";
/** Importe decimal con hasta dos decimales, con signo (una nota de crédito los lleva negativos). */
export const MoneyStringSchema = z.string().regex(/^-?\d{1,12}(\.\d{1,2})?$/, "Importe con formato inválido.");
/** Precio unitario: hasta cuatro decimales (precios de excedente pequeños). */
export const UnitAmountStringSchema = z.string().regex(/^-?\d{1,12}(\.\d{1,4})?$/, "Precio unitario con formato inválido.");
export const CurrencySchema = z.string().regex(/^[A-Z]{3}$/, "La moneda es un código ISO de tres letras en mayúsculas.");
export const InvoiceKindSchema = z.enum(["invoice", "credit_note"]);
export const InvoiceStatusSchema = z.enum(["draft", "open", "paid", "void", "uncollectible"]);
export const InvoiceLineKindSchema = z.enum(["flat", "per_seat", "overage", "adjustment"]);
export const PaymentStatusSchema = z.enum(["pending", "succeeded", "failed", "refunded"]);
export const InvoiceLineSchema = z.object({
    id: z.uuid(),
    subscriptionId: z.uuid().nullable(),
    moduleCode: z.string().nullable(),
    planCode: z.string().nullable(),
    kind: InvoiceLineKindSchema,
    description: z.string(),
    quantity: MoneyStringSchema,
    unitAmount: UnitAmountStringSchema,
    amount: MoneyStringSchema,
    taxAmount: MoneyStringSchema,
    periodStart: z.iso.datetime().nullable(),
    periodEnd: z.iso.datetime().nullable(),
    limitKey: z.string().nullable(),
});
export const PaymentSchema = z.object({
    id: z.uuid(),
    invoiceId: z.uuid(),
    gateway: z.string(),
    gatewayPaymentId: z.string().nullable(),
    status: PaymentStatusSchema,
    amount: MoneyStringSchema,
    currency: CurrencySchema,
    receivedAt: z.iso.datetime(),
    failureCode: z.string().nullable(),
    failureMessage: z.string().nullable(),
});
export const InvoiceSchema = z.object({
    id: z.uuid(),
    orgId: z.uuid(),
    kind: InvoiceKindSchema,
    /** `null` mientras está en `draft`: el correlativo se consume al emitir. */
    number: z.string().nullable(),
    status: InvoiceStatusSchema,
    currency: CurrencySchema,
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    issuedAt: z.iso.datetime().nullable(),
    dueAt: z.iso.datetime().nullable(),
    paidAt: z.iso.datetime().nullable(),
    taxRate: z.string(),
    subtotal: MoneyStringSchema,
    taxTotal: MoneyStringSchema,
    total: MoneyStringSchema,
    amountPaid: MoneyStringSchema,
    relatedInvoiceId: z.uuid().nullable(),
    /** Por qué sigue en `draft`; es lo que un operador tiene que arreglar para que se emita. */
    draftReason: z.string().nullable(),
    notes: z.string().nullable(),
    updatedAt: z.iso.datetime(),
});
/** Factura con su detalle: lo que devuelve `GET /invoices/:id`. */
export const InvoiceDetailSchema = InvoiceSchema.extend({
    lines: z.array(InvoiceLineSchema),
    payments: z.array(PaymentSchema),
});
export const BillingProfileSchema = z.object({
    orgId: z.uuid(),
    billingEmail: z.email().nullable(),
    taxIdType: z.string().nullable(),
    address: z.string().nullable(),
    gateway: z.string().nullable(),
    gatewayCustomerId: z.string().nullable(),
    /** Congela la mora de esta organización: el ciclo la salta. La reactivación sigue funcionando. */
    dunningPaused: z.boolean(),
    updatedAt: z.iso.datetime(),
});
export const UpsertBillingProfileInputSchema = z.object({
    billingEmail: z.email().nullable().optional(),
    taxIdType: z.string().max(16).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    dunningPaused: z.boolean().optional(),
});
export const PaymentMethodSchema = z.object({
    id: z.uuid(),
    orgId: z.uuid(),
    gateway: z.string(),
    brand: z.string().nullable(),
    /** Cuatro dígitos. Nunca el número completo ni el CVV: ver docs/FASE-6.md §0.5. */
    last4: z.string().nullable(),
    expMonth: z.number().int().min(1).max(12).nullable(),
    expYear: z.number().int().nullable(),
    status: z.enum(["active", "expired", "removed"]),
    isDefault: z.boolean(),
});
/**
 * Registro manual de un pago. `idempotencyKey` no es opcional a propósito: reintentar un POST que
 * ya se aplicó no puede volver a saldar la factura.
 */
export const RegisterPaymentInputSchema = z.object({
    amount: MoneyStringSchema,
    currency: CurrencySchema,
    idempotencyKey: z.string().min(8).max(200),
    gateway: z.string().regex(PATTERNS.KEY).optional(),
    gatewayPaymentId: z.string().max(200).nullable().optional(),
    receivedAt: z.iso.datetime().optional(),
    notes: z.string().max(500).optional(),
});
export const CreditNoteLineInputSchema = z.object({
    description: z.string().min(1).max(300),
    /** Positivo: el signo lo pone el núcleo al escribir la nota. */
    amount: MoneyStringSchema.refine((v) => !v.startsWith("-"), "El importe de una nota de crédito se indica en positivo."),
});
export const CreateCreditNoteInputSchema = z.object({
    invoiceId: z.uuid(),
    reason: z.string().min(1).max(500),
    lines: z.array(CreditNoteLineInputSchema).min(1).max(50),
});
export const VoidInvoiceInputSchema = z.object({
    reason: z.string().min(1).max(500),
});
/** Resumen de un ciclo: lo que devuelve `POST /billing/cycle` y lo que imprime el CLI. */
export const BillingCycleSummarySchema = z.object({
    periodsOpened: z.number().int().nonnegative(),
    periodsClosed: z.number().int().nonnegative(),
    invoicesIssued: z.number().int().nonnegative(),
    markedPastDue: z.number().int().nonnegative(),
    suspended: z.number().int().nonnegative(),
    reactivated: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    errors: z.array(z.object({ orgId: z.uuid(), error: z.string() })),
});
//# sourceMappingURL=billing.js.map